import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  FormControl,
  Select as MuiSelect,
  Typography,
} from '@material-ui/core';
import DropDownSvg from '../../../assets/icons/select-chevron-down.svg';
import { SELECT_AN_OPTION_TEXT } from '../../../assets/copy';

const useStyles = makeStyles((theme) => ({
  dropDown: {
    top: 'auto',
    margin: theme.spacing(3, 2.5),
  },
  root: {
    minWidth: 175,
    width: '100%',
    flex: 1,
    '& .MuiOutlinedInput-input': {
      ...theme.typography.medium1Normal,
      boxSizing: 'border-box',
      height: 'fit-content',
      padding: theme.spacing(1.5, 6, 1.5, 2),
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
      '& fieldset': {
        borderColor: theme.palette.digitalGray[60],
      },
    },
    msOverflowStyle: 'none' /* Internet Explorer 10+ */,
    scrollbarWidth: 'none' /* hide scrollbar on Firefox */,
  },
  label: {
    ...theme.typography.small4Normal,
    paddingBottom: theme.spacing(0.25),
  },
  placeholder: {
    ...theme.typography.small4Normal,
    color: '#687680',
  },
  listItem: {
    ...theme.typography.medium1Normal,
  },
  select: {
    borderRadius: 4,
    border: '2px solid #0D74AF',
    '& ul': {
      padding: theme.spacing(0),
      maxWidth: '100%',
      backgroundColor: theme.palette.background.white,
      ...theme.typography.medium1Normal,
      borderRadius: '4px',
      listStyleType: 'none',
    },
    '& option': {
      padding: theme.spacing(1, 1),
      '&:hover': {
        backgroundColor: theme.palette.digitalGray[10],
      },
      borderBottom: '1px solid #C9CED2',
      msOverflowStyle: 'none' /* Internet Explorer 10+ */,
      scrollbarWidth: 'none' /* hide scrollbar on Firefox */,
    },
  },
}));

/**
 * Select component for rendering a dropdown menu.
 *
 * *Note: If rendering a native select, the children should be <option> elements. Otherwise, the children should be <MenuItem> elements.*
 */
export const Select = ({
  classes: classesOverride,
  label,
  ariaLabel,
  onChange,
  value = '',
  children,
  native = true,
  placeholder,
}) => {
  const classes = useStyles({ classes: classesOverride });
  const dropdownIcon = (selectProps) => (
    <img
      src={DropDownSvg}
      className={clsx(classes.dropDown, selectProps.className)}
      alt={'drop-down-icon'}
    />
  );
  return (
    <FormControl variant="outlined" className={classes.root}>
      {label && <Typography className={classes.label}>{label}</Typography>}
      <MuiSelect
        native={native}
        value={value}
        onChange={onChange}
        inputProps={{ 'aria-label': ariaLabel }}
        IconComponent={dropdownIcon}
        MenuProps={{ classes: { paper: classes.select } }}
        displayEmpty
        renderValue={(value) => {
          if (value === '') {
            return (
              <Typography className={classes.placeholder}>
                {placeholder ?? SELECT_AN_OPTION_TEXT}
              </Typography>
            );
          }
          return <Typography className={classes.listItem}>{value}</Typography>;
        }}>
        {children}
      </MuiSelect>
    </FormControl>
  );
};

export default Select;

Select.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
};
